import { Divider, Stack, Typography } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { SummaryCard, SummaryCardRow } from "Components/_Shared/SummaryCard";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import type { FC } from "react";
import { AssetSummaryChart } from "./AssetSummaryChart";

type Props = {
  readonly immovableAssetsAmount: number;
  readonly movableAssetsAmount: number;
  readonly financialAssetsAmount: number;
  readonly liabilitiesAmount: number;
  readonly unusableAmount: number;
  readonly isTablet?: boolean;
};

export const AssetSummary: FC<Props> = ({
  immovableAssetsAmount,
  movableAssetsAmount,
  financialAssetsAmount,
  liabilitiesAmount,
  unusableAmount,
  isTablet,
}) => {
  const { t } = useResource();

  const availableAmount = immovableAssetsAmount + movableAssetsAmount + financialAssetsAmount - unusableAmount;

  return (
    <Stack spacing={Spacing.XL} pt={isTablet ? Spacing.XL : 0} pb={Spacing.XXXL} px={isTablet ? 0 : Spacing.XXXL} sx={{ width: 400 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {t(Resources.AnalysisDetail.Assets.AssetSummary.Title)}
      </Typography>
      <AssetSummaryChart
        immovableAssetsAmount={immovableAssetsAmount}
        movableAssetsAmount={movableAssetsAmount}
        financialAssetsAmount={financialAssetsAmount}
        liabilitiesAmount={liabilitiesAmount}
      />
      <SummaryCard>
        <SummaryCardRow
          color={"info.light"}
          value={formatCurrencyWithoutCode(immovableAssetsAmount)}
          units={t(Resources.Common.CZKSuffix)}
          label={t(Resources.AnalysisDetail.Assets.AssetSummary.Immovable)}
        />
        <SummaryCardRow
          color={"info.main"}
          value={formatCurrencyWithoutCode(movableAssetsAmount)}
          units={t(Resources.Common.CZKSuffix)}
          label={t(Resources.AnalysisDetail.Assets.AssetSummary.Movable)}
        />
        <SummaryCardRow
          color={colors.chartInfoDark}
          value={formatCurrencyWithoutCode(financialAssetsAmount)}
          units={t(Resources.Common.CZKSuffix)}
          label={t(Resources.AnalysisDetail.Assets.AssetSummary.Financial)}
        />
        <Divider />
        <SummaryCardRow
          color={"grey.100"}
          value={`${unusableAmount > 0 ? "- " : ""}${formatCurrencyWithoutCode(unusableAmount)}`}
          units={t(Resources.Common.CZKSuffix)}
          label={t(Resources.AnalysisDetail.Assets.AssetSummary.Unusable)}
        />
        <Divider />
        <SummaryCardRow
          value={formatCurrencyWithoutCode(availableAmount)}
          units={t(Resources.Common.CZKSuffix)}
          label={t(Resources.AnalysisDetail.Assets.AssetSummary.Available)}
          colorValue="success.main"
        />
      </SummaryCard>
      <SummaryCard>
        <SummaryCardRow
          color="error.main"
          value={formatCurrencyWithoutCode(liabilitiesAmount)}
          units={t(Resources.Common.CZKSuffix)}
          label={t(Resources.AnalysisDetail.Assets.AssetSummary.Liabilities)}
        />
      </SummaryCard>
    </Stack>
  );
};
